import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  ListGroup,
  Form,
  Badge,
  Spinner,
  Table,
  FloatingLabel,
} from "react-bootstrap";

const COLORS = {
  blue: "#0062A0",
  magenta: "#E91E63",
  lightBg: "#f4f7f9",
  sidebarBg: "#ffffff",
};

const API_URL = "http://radheyelectical.atwebpages.com/api/api_handler.php";

/**
 * CONFIGURATION: Define which fields show up for which category
 * Add or remove categories/fields here to update the UI instantly.
 */
const CATEGORY_FIELDS = {
  hero: ["title", "subtitle", "image_url", "sort_order"],
  about: ["title", "subtitle", "description", "image_url"],
  projects: ["title", "description", "image_url", "badge", "sort_order"],
  testimonials: ["title", "subtitle", "description", "sort_order"],
  contact: ["title", "contact_email", "map_url", "extra_info"],
  gallery: ["title", "image_url", "sort_order"],
  default: [
    "title",
    "subtitle",
    "description",
    "badge",
    "image_url",
    "icon_name",
    "sort_order",
  ],
};

const AdminHomeManager = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    category: "",
    badge: "",
    title: "",
    subtitle: "",
    description: "",
    image_url: "",
    icon_name: "",
    contact_email: "",
    extra_info: "",
    sort_order: 0,
    map_url: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(API_URL);
      const result = await response.json();
      if (result.status === "success") {
        const filtered = Object.keys(result.data)
          .filter(
            (cat) =>
              !["partner", "service", "expertise"].includes(cat.toLowerCase())
          )
          .reduce((obj, key) => {
            obj[key] = result.data[key];
            return obj;
          }, {});

        setData(filtered);
        setSelectedCat((prev) => prev || Object.keys(filtered)[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenForm = useCallback(
    (item = null) => {
      if (item) {
        setFormData(item);
        setIsEditing(true);
      } else {
        setFormData({
          category: selectedCat || "",
          badge: "",
          title: "",
          subtitle: "",
          description: "",
          image_url: "",
          icon_name: "",
          contact_email: "",
          extra_info: "",
          sort_order: 0,
          map_url: "",
        });
        setIsEditing(false);
      }
      setShowForm(true);
    },
    [selectedCat]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const action = isEditing ? "update" : "insert";
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, action }),
        });
        const result = await response.json();
        if (result.status === "success") {
          fetchData();
          setShowForm(false);
        }
      } catch (err) {
        alert("Error saving data");
      }
    },
    [formData, isEditing, fetchData]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (window.confirm("Delete this entry permanently?")) {
        await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "delete", id }),
        });
        fetchData();
      }
    },
    [fetchData]
  );

  // Helper to determine which fields to render
  const activeFields = useMemo(() => {
    if (!selectedCat) return [];
    const catKey = selectedCat.toLowerCase();
    return CATEGORY_FIELDS[catKey] || CATEGORY_FIELDS.default;
  }, [selectedCat]);

  // Dynamic Input Renderer
  const renderDynamicField = (fieldName) => {
    const labels = {
      title: "Main Title",
      subtitle: "Sub-heading / Designation",
      description: "Detailed Content",
      image_url: "Image Link (URL)",
      sort_order: "Display Priority (Number)",
      badge: "Badge / Tag",
      contact_email: "Business Email",
      map_url: "Google Maps Embed URL",
      extra_info: "Additional Notes",
      icon_name: "Icon Class Name",
    };

    const isTextArea = ["description", "extra_info", "map_url"].includes(
      fieldName
    );

    return (
      <Col
        md={fieldName === "description" || fieldName === "image_url" ? 12 : 6}
        key={fieldName}
      >
        <FloatingLabel label={labels[fieldName] || fieldName}>
          <Form.Control
            as={isTextArea ? "textarea" : "input"}
            style={isTextArea ? { height: "100px" } : {}}
            type={fieldName === "sort_order" ? "number" : "text"}
            placeholder={fieldName}
            value={formData[fieldName] || ""}
            onChange={(e) =>
              setFormData({ ...formData, [fieldName]: e.target.value })
            }
            required={fieldName === "title"}
          />
        </FloatingLabel>
      </Col>
    );
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" style={{ color: COLORS.blue }} />
      </div>
    );

  return (
    <Container
      fluid
      className="p-0"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <Row className="g-0" style={{ height: "100%" }}>
        {/* SIDEBAR */}
        <Col
          md={4}
          lg={3}
          className="border-end shadow-sm"
          style={{ backgroundColor: COLORS.sidebarBg, zIndex: 10 }}
        >
          <div className="p-4 border-bottom bg-white">
            <h5 className="fw-bold mb-0" style={{ color: COLORS.blue }}>
              ADMIN CORE
            </h5>
            <small className="text-muted">Content Management</small>
          </div>
          <div
            className="p-3 overflow-auto"
            style={{ maxHeight: "calc(100vh - 100px)" }}
          >
            <ListGroup variant="flush">
              {Object.keys(data).map((cat) => (
                <ListGroup.Item
                  key={cat}
                  action
                  onClick={() => setSelectedCat(cat)}
                  className={`border-0 rounded-3 mb-1 px-3 py-3 transition-all ${
                    selectedCat === cat ? "shadow-sm" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedCat === cat ? COLORS.blue : "transparent",
                    color: selectedCat === cat ? "#fff" : "#444",
                  }}
                >
                  <i
                    className={`bi bi-folder2${
                      selectedCat === cat ? "-open" : ""
                    } me-2`}
                  ></i>
                  <span className="text-capitalize">{cat}</span>
                  <Badge
                    pill
                    bg={selectedCat === cat ? "light" : "secondary"}
                    text={selectedCat === cat ? "dark" : "white"}
                    className="float-end mt-1"
                  >
                    {data[cat].length}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </Col>

        {/* MAIN WORKSPACE */}
        <Col
          md={8}
          lg={9}
          style={{ backgroundColor: COLORS.lightBg, overflowY: "auto" }}
        >
          <div className="p-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="fw-bold text-dark mb-0 text-capitalize">
                  {selectedCat}
                </h2>
                <p className="text-muted small">
                  Viewing relevant fields for this data group
                </p>
              </div>
              <Button
                onClick={() => handleOpenForm()}
                style={{
                  backgroundColor: COLORS.magenta,
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 25px",
                }}
              >
                + Add New {selectedCat}
              </Button>
            </div>

            <Card className="border-0 shadow-sm rounded-4">
              <Table hover responsive className="mb-0 align-middle">
                <thead className="bg-white">
                  <tr>
                    <th className="ps-4 py-3 border-0 text-muted small">
                      ORDER
                    </th>
                    <th className="py-3 border-0 text-muted small">TITLE</th>
                    <th className="py-3 border-0 text-muted small text-end pe-4">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data[selectedCat]?.map((item) => (
                    <tr key={item.id}>
                      <td className="ps-4 fw-bold text-muted">
                        #{item.sort_order}
                      </td>
                      <td className="fw-bold">{item.title}</td>
                      <td className="text-end pe-4">
                        <Button
                          variant="link"
                          className="text-primary text-decoration-none me-3"
                          onClick={() => handleOpenForm(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          className="text-danger text-decoration-none"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </div>
        </Col>
      </Row>

      {/* DYNAMIC MODAL */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="border-0 px-4 pt-4">
            <Modal.Title className="fw-bold">
              {isEditing ? `Edit ${selectedCat}` : `New ${selectedCat} Entry`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4">
            <Row className="g-3">
              {/* Always include category as a hidden/disabled field */}
              <Col md={12}>
                <FloatingLabel label="Current Category">
                  <Form.Control disabled value={formData.category} />
                </FloatingLabel>
              </Col>

              {/* Render only fields defined in CATEGORY_FIELDS */}
              {activeFields.map((field) => renderDynamicField(field))}
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 px-4 pb-4">
            <Button
              variant="light"
              className="px-4 text-muted"
              onClick={() => setShowForm(false)}
            >
              Discard
            </Button>
            <Button
              type="submit"
              style={{
                backgroundColor: COLORS.blue,
                border: "none",
                padding: "10px 40px",
                borderRadius: "10px",
              }}
            >
              {isEditing ? "Update Data" : "Save Item"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .transition-all { transition: all 0.2s; }
        .form-control { border-radius: 10px; border: 1px solid #dee2e6; }
        .form-control:focus { border-color: ${COLORS.blue}; box-shadow: 0 0 0 0.25rem ${COLORS.blue}20; }
      `}</style>
    </Container>
  );
};

export default AdminHomeManager;
